import { isAssessmentDomain } from './recordingContract.js';
import { recordFieldValue } from './recordFieldValue.js';
import { recordInputDomain, hasRecordInputSchema, recordFieldApplies, recordFieldMaximum } from './recordInputSchema.js';
import { legacyEventValues } from './domainEventPresentation.js';
import { getPublishedResultPresentation } from './resultEventPresentation.js';
import { isCurrentDomainRecord } from './recordTimeline.js';
import { numericTotalFields, numericRecordTotal } from './numericTotal.js';

/** Shared collection adapters may contain definitions as well as actual events. */
export function isReportingEvent(event, domain) {
  return isCurrentDomainRecord(event, domain) && matchesDomain(event, domain)
    && (domain?.runtime?.adapter !== 'recognition' || Boolean(event?.studentId));
}

/** Published assessments retain their own identities, maxima and ordered result labels. */
export function publishedAssessmentFacts(events = [], { includeRecords = false } = {}) {
  const groups = new Map();
  for (const event of events) {
    const result = getPublishedResultPresentation(event);
    if (!result) continue;
    const key = JSON.stringify([event.programmeId, event.assessmentId]);
    const group = groups.get(key) || {
      key, label: [event.subject, event.assessmentName].filter(Boolean).join(' · '),
      programme: event.programmeName, count: 0, percentages: [], results: [], distribution: new Map(),
    };
    const percentage = event.resultEntry.kind === 'points' && Number.isFinite(event.resultEntry.value)
      && Number.isFinite(event.maxScore) && event.maxScore > 0
      ? event.resultEntry.value / event.maxScore * 100 : null;
    group.count += 1;
    group.distribution.set(result.label, (group.distribution.get(result.label) || 0) + 1);
    if (percentage != null) group.percentages.push(percentage);
    if (includeRecords) group.results.push({ id: event.id, studentId: event.studentId, studentName: event.studentName,
      classId: event.classId, className: event.className, label: result.label, percentage });
    groups.set(key, group);
  }
  return [...groups.values()].map(({ percentages, results, distribution, ...group }) => ({
    ...group,
    average: percentages.length ? percentages.reduce((sum, value) => sum + value, 0) / percentages.length : null,
    measured: percentages.length,
    distribution: [...distribution].map(([name, count]) => ({ name, count })),
    ...(includeRecords ? { results } : {}),
  })).sort((a, b) => a.label.localeCompare(b.label));
}

export const REPORTING_DATE_FIELDS = ['issuedAt', 'createdAt', 'occurredAt', 'date', 'timestamp'];

/** One chronology for charts, Records-linked summaries and scheduled outputs. */
export function eventDate(event) {
  const value = REPORTING_DATE_FIELDS.map(field => event?.[field]).find(value => value != null);
  if (value == null) return null;
  const date = typeof value?.toDate === 'function' ? value.toDate()
    : typeof value?.toMillis === 'function' ? new Date(value.toMillis())
      : typeof value?.seconds === 'number' ? new Date(value.seconds * 1000)
        : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function collectiveScope(domainConfig) {
  const declaration = domainConfig?.capabilities?.collective;
  return typeof declaration === 'object' ? declaration.scope : null;
}

export function matchesDomain(event, domainConfig) {
  if (collectiveScope(domainConfig) === 'collection') return true;
  if (!event?.domainId) return true;
  return event.domainId === domainConfig?.id || event.domainId === domainConfig?.name;
}

export function reportingFieldValue(event, field, domainConfig) {
  if (!field) return undefined;
  if (field === 'value' && domainConfig?.runtime?.adapter === 'recognition') {
    const values = legacyEventValues(event);
    return values.length ? values : undefined;
  }
  domainConfig = recordInputDomain(event, domainConfig);
  const savedValue = recordFieldValue(event, field, domainConfig);
  if (savedValue !== undefined) return savedValue;
  if (!domainConfig?.primary && domainConfig?.fieldType === 'number') {
    return event?.numericValue ?? event?.score;
  }
  return undefined;
}

const fieldValue = reportingFieldValue;

export function normalizedFieldType(field) {
  if (!field?.type && Array.isArray(field?.options)) return 'multiChoice';
  return field?.type === 'binary' ? 'boolean' : field?.type;
}

export function isCategoricalField(field) {
  return ['boolean', 'multiChoice'].includes(normalizedFieldType(field));
}

/**
 * Current fields plus fields actually offered on saved forms in this period.
 * A retired field is still relevant when reporting the records that used it.
 */
export function reportingFields(domainConfig, events = []) {
  const fields = new Map(currentReportingFields(domainConfig).map(field => [field.id, field]));
  for (const event of events) {
    if (!hasRecordInputSchema(event)) continue;
    for (const field of currentReportingFields(recordInputDomain(event, domainConfig))) {
      if (!fields.has(field.id)) fields.set(field.id, field);
    }
  }
  return [...fields.values()];
}

function currentReportingFields(domainConfig) {
  // The assessment result is defined by its Programme, not the Domain editor's
  // placeholder primary field. Additional questions keep their own units.
  if (isAssessmentDomain(domainConfig)) {
    return (domainConfig.followUp || []).filter(field => field?.retired !== true && field?.active !== false);
  }
  if (domainConfig?.primary) {
    return [domainConfig.primary, ...(domainConfig.followUp || [])]
      .filter((field) => field?.retired !== true && field?.active !== false);
  }
  if (Array.isArray(domainConfig?.fields) && domainConfig.fields.length > 0) {
    return domainConfig.fields
      .filter((field) => field?.retired !== true && field?.active !== false);
  }
  // Compatibility with the protected value-based event writer. This adapts
  // its stored field, not a separate dashboard or a school form definition.
  if (domainConfig?.runtime?.adapter === 'recognition') {
    return [{ id: 'value', label: 'Value', type: 'multiChoice' },
      { id: 'points', label: 'Recorded points', type: 'number' }];
  }
  const type = domainConfig?.fieldType === 'binary'
    ? 'boolean'
    : domainConfig?.fieldType;
  if (!['boolean', 'multiChoice', 'number', 'string', 'text', 'storageRef', 'rating'].includes(type)) {
    return [];
  }
  return [{
    id: 'event',
    label: domainConfig?.terminology?.event?.label || 'Event',
    type,
    ...(type === 'number' && domainConfig?.maxScore
      ? { maxScore: domainConfig.maxScore }
      : {}),
  }];
}

/**
 * The measures the shared UI and runtime can actually calculate for a Domain's
 * active field types. Totals require explicitly configured contributions.
 */
export function reportingProfile(domainConfig) {
  const fields = reportingFields(domainConfig);
  const numberFields = fields.filter((field) => normalizedFieldType(field) === 'number');
  const hasCategorical = fields.some((field) => (
    ['boolean', 'multiChoice'].includes(normalizedFieldType(field))
  ));
  const hasText = fields.some((field) => (
    ['string', 'text', 'storageRef'].includes(normalizedFieldType(field))
  ));
  const measures = [];
  const add = (key, label) => {
    if (!measures.some((measure) => measure.key === key)) measures.push({ key, label });
  };

  if (hasCategorical) {
    add('count', 'Totals');
    add('percent', 'Percentages');
    add('breakdown', 'Choice breakdowns');
    add('trend', 'Trends');
  }
  if (numberFields.length > 0) {
    add('count', 'Entries');
    add('score', isAssessmentDomain(domainConfig) ? 'Question totals' : 'Scores');
    add('maximum', isAssessmentDomain(domainConfig) ? 'Question maxima' : 'Maximum scores');
    add('average', 'Averages');
    if (numericTotalFields(domainConfig).length >= 2) {
      add('weighting', 'Weighted totals');
    }
    add('trend', 'Trends');
  }
  if (hasText) {
    add('count', 'Completion totals');
    add('trend', 'Trends');
  }
  if ((fields.length > 0 || isAssessmentDomain(domainConfig)) && measures.length === 0) {
    add('count', 'Totals');
    add('trend', 'Trends');
  }
  return measures;
}

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function maximumForEvent(event, field, domainConfig) {
  return recordFieldMaximum(event, field?.id, domainConfig);
}

/** Same numeric observations drive totals, charts and the underlying record list. */
export function numericFieldObservations(domainConfig, field, events = []) {
  return events.flatMap(event => {
    if (!recordFieldApplies(event, field, domainConfig)) return [];
    const value = finiteNumber(fieldValue(event, field.id, domainConfig));
    if (value == null) return [];
    return [{ id: event.id, studentId: event.studentId, studentName: event.studentName,
      classId: event.classId, className: event.className, value,
      maximum: maximumForEvent(event, field, domainConfig) }];
  });
}

/**
 * Aggregate numeric values without turning each score into a categorical
 * bucket. Each field keeps its own units. Cross-field weighting is normalized
 * to percentages first, so differently-scaled fields are never added raw.
 */
export function aggregateNumericMeasures(domainConfig, events = []) {
  const fields = reportingFields(domainConfig, events)
    .filter((field) => normalizedFieldType(field) === 'number');
  const fieldSummaries = fields.map((field) => {
    const observations = numericFieldObservations(domainConfig, field, events);
    const score = observations.length > 0
      ? observations.reduce((sum, observation) => sum + observation.value, 0)
      : null;
    const maximaComplete = observations.length > 0
      && observations.every((observation) => observation.maximum != null);
    const maximum = maximaComplete
      ? observations.reduce((sum, observation) => sum + observation.maximum, 0)
      : null;
    const percentValues = observations
      .filter((observation) => observation.maximum != null)
      .map((observation) => (observation.value / observation.maximum) * 100);

    return {
      fieldId: field.id,
      label: field.label || field.id,
      ...(isAssessmentDomain(domainConfig) ? { supplementary: true } : {}),
      count: observations.length,
      score,
      maximum,
      average: observations.length > 0 ? score / observations.length : null,
      averagePercent: percentValues.length === observations.length && observations.length > 0
        ? percentValues.reduce((sum, value) => sum + value, 0) / percentValues.length
        : null,
    };
  });

  const totals = events.map(event => numericRecordTotal(event, domainConfig)).filter(Boolean);
  const completeTotals = totals.filter(total => total.complete);
  const configurations = [...new Map(totals.map(total => [
    JSON.stringify(total.contributions.map(({ fieldId, weight }) => ({ fieldId, weight }))),
    total.contributions.map(({ fieldId, weight }) => ({ fieldId, weight })),
  ])).values()];
  const configured = numericTotalFields(domainConfig);
  const contributions = configurations.length === 1 ? configurations[0]
    : configurations.length === 0 ? configured.map(field => ({ fieldId: field.id, weight: field.weight })) : [];
  const score = completeTotals.length ? completeTotals.reduce((sum, total) => sum + total.value, 0) : null;
  const weighting = totals.length || configured.length ? {
    fieldIds: [...new Set(configurations.length ? configurations.flat().map(field => field.fieldId) : configured.map(field => field.id))],
    weights: Object.fromEntries(contributions.map(field => [field.fieldId, field.weight])),
    count: completeTotals.length,
    incomplete: totals.length - completeTotals.length,
    score,
    maximum: completeTotals.length ? completeTotals.length * 100 : null,
    average: completeTotals.length ? score / completeTotals.length : null,
    ...(configurations.length > 1 ? { configurations } : {}),
  } : null;

  return { fields: fieldSummaries, weighting };
}


export function optionLabel(domainConfig, field, value) {
  const candidates = [domainConfig?.primary, ...(domainConfig?.followUp || []), ...(domainConfig?.fields || [])];
  const definition = candidates.find((candidate) => candidate?.id === field);
  const option = definition?.options?.find((candidate) => candidate?.value === value);
  if (option?.label) return option.label;
  if (normalizedFieldType(definition) === 'boolean' && typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === undefined || value === null || value === '') return 'Not recorded';
  return String(value).replace(/[-_]/g, ' ').replace(/^./, (char) => char.toUpperCase());
}

export function categoricalBreakdown(domainConfig, field, events) {
  if (!isCategoricalField(field)) return [];
  const counts = new Map();
  for (const event of events) {
    if (!recordFieldApplies(event, field, domainConfig)) continue;
    const answer = fieldValue(event, field.id, domainConfig);
    const answers = Array.isArray(answer) ? [...new Set(answer)] : [answer];
    if (answers.length === 0) answers.push(undefined);
    for (const raw of answers) {
      const label = optionLabel(recordInputDomain(event, domainConfig), field.id, raw);
      const key = JSON.stringify([raw ?? null, label]);
      const current = counts.get(key) || {
        value: raw ?? null,
        label,
        count: 0,
      };
      current.count += 1;
      counts.set(key, current);
    }
  }
  return [...counts.values()].sort((left, right) => (
    right.count - left.count || left.label.localeCompare(right.label)
  ));
}
